import React, { useState } from 'react';
import styled from 'styled-components';
import { Search, Package, Calendar, MapPin, Phone, Mail, CheckCircle, Clock, Truck, XCircle } from 'lucide-react';
import { trackOrder, Order } from '../services/orderService';
import { Timestamp } from 'firebase/firestore';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: #f5f5f5;
  width: 100%;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
  font-weight: 300;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 40px;
  font-size: 1.1rem;
`;

const SearchContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const SearchButton = styled.button`
  padding: 15px 30px;
  background: #000;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;
  
  &:hover {
    background: #333;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const OrderCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const OrderNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #000;
`;

const StatusBadge = styled.div<{ $status: string }>`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => {
    switch (props.$status) {
      case 'Alındı':
        return 'background: #fff3cd; color: #856404;';
      case 'Hazırlanıyor':
        return 'background: #cfe2ff; color: #084298;';
      case 'Kargolandı':
        return 'background: #d1e7dd; color: #0f5132;';
      case 'Teslim Edildi':
        return 'background: #d1e7dd; color: #0f5132;';
      case 'İptal Edildi':
        return 'background: #f8d7da; color: #842029;';
      default:
        return 'background: #e9ecef; color: #495057;';
    }
  }}
`;

const InfoSection = styled.div`
  margin-bottom: 25px;
  
  h3 {
    font-size: 1.1rem;
    color: #333;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  
  .label {
    font-size: 0.85rem;
    color: #666;
  }
  
  .value {
    font-size: 1rem;
    color: #333;
    font-weight: 500;
  }
`;

const ItemsList = styled.div`
  margin-top: 15px;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
  
  img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  
  .name {
    font-weight: 600;
    color: #333;
    margin-bottom: 5px;
  }
  
  .details {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 5px;
  }
  
  .price {
    font-weight: 600;
    color: #000;
  }
`;

const TotalAmount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 2px solid #f0f0f0;
  font-size: 1.3rem;
  font-weight: 600;
  
  .label {
    color: #666;
  }
  
  .value {
    color: #000;
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #842029;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

const OrderTrackingPage: React.FC = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orderNumber.trim()) {
      setError('Lütfen sipariş numaranızı giriniz');
      return;
    }

    setLoading(true);
    setError(null);
    setOrder(null);

    try {
      const foundOrder = await trackOrder(orderNumber.trim());
      
      if (foundOrder) {
        setOrder(foundOrder);
      } else {
        setError('Böyle bir sipariş bulunamadı. Lütfen sipariş numaranızı kontrol ediniz.');
      }
    } catch (err: any) {
      setError('Sipariş sorgulanırken bir hata oluştu. Lütfen tekrar deneyiniz.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Alındı':
        return <Clock size={16} />;
      case 'Hazırlanıyor':
        return <Package size={16} />;
      case 'Kargolandı':
        return <Truck size={16} />;
      case 'Teslim Edildi':
        return <CheckCircle size={16} />;
      case 'İptal Edildi':
        return <XCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  return (
    <PageContainer>
      <Container>
        <Title>Sipariş Takip</Title>
        <Subtitle>Sipariş numaranız ile siparişinizin durumunu öğrenebilirsiniz</Subtitle>

        <SearchContainer>
          <SearchForm onSubmit={handleSearch}>
            <SearchInput
              type="text"
              placeholder="Sipariş Numaranız (örn: SM-123456)"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
              maxLength={9}
            />
            <SearchButton type="submit" disabled={loading}>
              <Search size={20} />
              {loading ? 'Aranıyor...' : 'Sorgula'}
            </SearchButton>
          </SearchForm>
        </SearchContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {order && (
          <OrderCard>
            <OrderHeader>
              <OrderNumber>{order.orderNumber}</OrderNumber>
              <StatusBadge $status={order.status}>
                {getStatusIcon(order.status)}
                {order.status}
              </StatusBadge>
            </OrderHeader>

            <InfoSection>
              <h3>
                <MapPin size={20} />
                Teslimat Bilgileri
              </h3>
              <InfoGrid>
                <InfoItem>
                  <span className="label">Ad Soyad</span>
                  <span className="value">
                    {order.customer.name} {order.customer.surname}
                  </span>
                </InfoItem>
                <InfoItem>
                  <span className="label">
                    <Phone size={14} style={{ marginRight: '5px' }} />
                    Telefon
                  </span>
                  <span className="value">{order.customer.phone}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">
                    <Mail size={14} style={{ marginRight: '5px' }} />
                    E-posta
                  </span>
                  <span className="value">{order.customer.email}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">Adres</span>
                  <span className="value">{order.customer.address}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">Şehir</span>
                  <span className="value">{order.customer.city}</span>
                </InfoItem>
                {order.customer.postalCode && (
                  <InfoItem>
                    <span className="label">Posta Kodu</span>
                    <span className="value">{order.customer.postalCode}</span>
                  </InfoItem>
                )}
              </InfoGrid>
            </InfoSection>

            <InfoSection>
              <h3>
                <Package size={20} />
                Sipariş Detayları
              </h3>
              <ItemsList>
                {order.items.map((item, index) => (
                  <ItemCard key={index}>
                    {item.image && (
                      <img src={item.image} alt={item.name} />
                    )}
                    <ItemInfo>
                      <div className="name">{item.name}</div>
                      <div className="details">
                        Adet: {item.quantity}
                        {item.size && ` • Beden: ${item.size}`}
                      </div>
                      <div className="price">
                        {(item.price * item.quantity).toFixed(2)} ₺
                      </div>
                    </ItemInfo>
                  </ItemCard>
                ))}
              </ItemsList>
              <TotalAmount>
                <span className="label">Toplam Tutar:</span>
                <span className="value">{order.totalAmount.toFixed(2)} ₺</span>
              </TotalAmount>
            </InfoSection>

            {order.createdAt && (
              <InfoSection>
                <h3>
                  <Calendar size={20} />
                  Tarih Bilgileri
                </h3>
                <InfoGrid>
                  <InfoItem>
                    <span className="label">Sipariş Tarihi</span>
                    <span className="value">
                      {(() => {
                        let date: Date;
                        if (order.createdAt instanceof Date) {
                          date = order.createdAt;
                        } else if (order.createdAt instanceof Timestamp) {
                          date = order.createdAt.toDate();
                        } else {
                          date = new Date(order.createdAt);
                        }
                        return date.toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        });
                      })()}
                    </span>
                  </InfoItem>
                  {order.updatedAt && (
                    <InfoItem>
                      <span className="label">Son Güncelleme</span>
                      <span className="value">
                        {(() => {
                          let date: Date;
                          if (order.updatedAt instanceof Date) {
                            date = order.updatedAt;
                          } else if (order.updatedAt instanceof Timestamp) {
                            date = order.updatedAt.toDate();
                          } else {
                            date = new Date(order.updatedAt);
                          }
                          return date.toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                        })()}
                      </span>
                    </InfoItem>
                  )}
                </InfoGrid>
              </InfoSection>
            )}
          </OrderCard>
        )}

        {!order && !loading && !error && (
          <EmptyState>
            <Package />
            <p>Sipariş numaranızı girerek sorgulama yapabilirsiniz</p>
          </EmptyState>
        )}
      </Container>
    </PageContainer>
  );
};

export default OrderTrackingPage;
